/**
 * ASTROFALL — first-playable vector cartridge (MVP).
 *
 * Heritage: Asteroids (1979) — rotational ship, momentum-forever physics,
 * splitting asteroids — plus Lunar Lander (1979) — thrust-based steering,
 * vector aesthetic. Original mechanic: **strategic hyperspace**. Pressing
 * hyperspace teleports the ship to the position of the OLDEST in-flight
 * bullet (consuming it). It's an offensive maneuver, not a panic button —
 * you fire toward a clear pocket, then port to it.
 *
 * Architecture:
 *   - 960x540 vector playfield, pure black, phosphor-green strokes
 *   - Single screen, no scroll. Ship + asteroids wrap at edges
 *   - Asteroids in 3 tiers: LARGE -> 2x MEDIUM -> 2x SMALL -> gone
 *   - Bullets are short-lived projectiles; max 4 on screen
 *   - Every 3rd wave (3, 6, 9, ...) a gravity well spawns at a random
 *     point and applies an inverse-square pull on everything until the
 *     wave clears
 *
 * Determinism: all randomness routes through ctx.random. Same (seed,
 * inputFrames) -> same final score; verified by the 1000-iter test.
 */

import type {
  GameContext,
  GameInstance,
  InputState,
  LoopHandle,
} from '@arcraz/arcadia-types';
import {
  BULLET,
  FIELD_BG,
  GRAVITY_WELL,
  GRAVITY_WELL_BLOOM,
  HUD_LIVES,
  HUD_SCORE,
  HUD_TEXT,
  HUD_WAVE,
  HYPERSPACE_FLASH,
  SHIP_BLOOM,
  SHIP_LINE,
  THRUSTER,
  VECTOR_BLOOM,
  VECTOR_LINE,
} from './palette.js';

// --- Field ------------------------------------------------------------
const WIDTH = 960;
const HEIGHT = 540;
const TWO_PI = Math.PI * 2;

// --- Ship -------------------------------------------------------------
const SHIP_RADIUS = 14; // half-length nose-to-tail; collision radius
const SHIP_ROT = 0.06; // radians per tick (~3.4 deg)
const SHIP_THRUST = 0.12;
const SHIP_MAX_VEL = 6;
const SHIP_FIRE_COOLDOWN = 9;

// --- Bullets ----------------------------------------------------------
const BULLET_MAX = 4;
const BULLET_VEL = 7;
const BULLET_LIFE = 50;
const BULLET_RADIUS = 2;

// --- Asteroids --------------------------------------------------------
// Tier indices: 0=large, 1=medium, 2=small.
const ASTEROID_RADIUS = [60, 35, 20] as const;
const ASTEROID_SCORE = [20, 50, 100] as const;
const ASTEROID_VERTS = [11, 9, 8] as const;
const ASTEROID_BASE_SPEED = 1.0;
const ASTEROID_SPEED_VAR = 0.8;

// --- Hyperspace -------------------------------------------------------
const HYPERSPACE_COOLDOWN = 120;
const HYPERSPACE_FLASH_TICKS = 14;

// --- Gravity well -----------------------------------------------------
// Inverse-square pull capped to avoid singularity blow-up at small r.
const GRAVITY_WAVE_EVERY = 3;
const GRAVITY_CONST = 1800;
const GRAVITY_MIN_R = 40; // r below this clamps to this (avoids infinity)
const GRAVITY_MAX_FORCE = 0.45;

// --- Waves ------------------------------------------------------------
const WAVE_START_COUNT = 4; // wave 1 spawns 4 large asteroids
const WAVE_GROWTH = 1; // wave N spawns WAVE_START_COUNT + (N-1) large
const WAVE_BEAT_TICKS = 90; // pause between waves
const WAVE_BANNER_TICKS = 80;

// --- Lives + game over ------------------------------------------------
const LIVES_START = 3;
const RESPAWN_INVULN = 60;
const CRASH_FLASH_TICKS = 22;

type Phase = 'playing' | 'crashed' | 'wave-clear' | 'game-over';

interface Vec {
  x: number;
  y: number;
}

interface Ship extends Vec {
  vx: number;
  vy: number;
  rot: number; // radians; 0 = pointing right, increases clockwise on screen
  invuln: number; // ticks of post-respawn invulnerability remaining
}

interface Bullet extends Vec {
  vx: number;
  vy: number;
  life: number;
  born: number; // tick at spawn; for "oldest in flight" lookup
}

interface Asteroid extends Vec {
  vx: number;
  vy: number;
  tier: 0 | 1 | 2;
  rot: number;
  rotVel: number;
  vertJitter: readonly number[]; // per-vertex radius multiplier, frozen at spawn
}

interface GravityWell extends Vec {
  active: boolean;
}

export class AstrofallInstance implements GameInstance {
  private readonly ctx: GameContext;

  // Entities.
  private ship: Ship;
  private bullets: Bullet[] = [];
  private asteroids: Asteroid[] = [];
  private well: GravityWell = { x: 0, y: 0, active: false };

  // Input mirror (set in input(), consumed in update()).
  private inLeft = false;
  private inRight = false;
  private inThrust = false;
  private firePressed = false;
  private hyperspacePressed = false;

  // Sustained voices. Thrust is a held-key event so it routes through
  // audio.loop() instead of sfx() — per-tick sfx() calls lawnmower the
  // sample bus. The loop is created lazily on first ever update() call
  // (first frame of the run) and stays alive for the cartridge's life;
  // gain is modulated to 0 when not thrusting / when crashed / when
  // game-over. teardown() stops it.
  private thrustLoop: LoopHandle | null = null;

  // Cooldowns / counters.
  private fireCooldown = 0;
  private hyperspaceCooldown = 0;
  private hyperspaceFlashLeft = 0;

  // Score + run state.
  private score = 0;
  private lives = LIVES_START;
  private wave = 1;
  private tickCount = 0;
  private phase: Phase = 'playing';
  private crashFlashLeft = 0;
  private waveBeatLeft = 0;
  private waveBannerLeft = WAVE_BANNER_TICKS;
  private finalScore = 0;
  private finalWave = 1;
  private restartArmed = false;

  constructor(ctx: GameContext) {
    this.ctx = ctx;
    this.ship = {
      x: WIDTH / 2,
      y: HEIGHT / 2,
      vx: 0,
      vy: 0,
      rot: -Math.PI / 2, // start pointing up
      invuln: RESPAWN_INVULN,
    };
    this.spawnWave(this.wave);
  }

  // --------------------------------------------------------------------------
  // GameInstance contract
  // --------------------------------------------------------------------------

  input(state: InputState): void {
    if (this.phase === 'game-over') {
      if (state.justPressed.has('fireA') || state.justPressed.has('start')) {
        this.restartArmed = true;
      }
      return;
    }
    this.inLeft = state.left;
    this.inRight = state.right;
    this.inThrust = state.up;
    this.firePressed = state.justPressed.has('fireA') || state.justPressed.has('start');
    // FireB = hyperspace. Edge-trigger so a held key doesn't chain teleports.
    this.hyperspacePressed = state.justPressed.has('fireB');
  }

  update(_dt: number): void {
    this.tickCount += 1;

    this.updateThrustAudio();

    if (this.phase === 'game-over') return;

    if (this.phase === 'crashed') {
      this.crashFlashLeft -= 1;
      if (this.crashFlashLeft <= 0) {
        if (this.lives <= 0) {
          this.phase = 'game-over';
          this.finalScore = Math.floor(this.score);
          this.finalWave = this.wave;
          this.ctx.events.emit('death', { remainingLives: 0 });
        } else {
          this.respawn();
          this.phase = 'playing';
        }
      }
      // Continue advancing asteroids so the field is alive during the flash.
      this.advanceAsteroids();
      this.advanceBullets();
      return;
    }

    if (this.phase === 'wave-clear') {
      this.waveBeatLeft -= 1;
      this.advanceAsteroids(); // empty by definition, but keeps field state coherent
      this.advanceBullets();
      if (this.waveBeatLeft <= 0) {
        this.wave += 1;
        this.spawnWave(this.wave);
        this.phase = 'playing';
        this.waveBannerLeft = WAVE_BANNER_TICKS;
      }
      return;
    }

    // --- Playing phase ----
    if (this.hyperspaceCooldown > 0) this.hyperspaceCooldown -= 1;
    if (this.hyperspaceFlashLeft > 0) this.hyperspaceFlashLeft -= 1;
    if (this.fireCooldown > 0) this.fireCooldown -= 1;
    if (this.waveBannerLeft > 0) this.waveBannerLeft -= 1;
    if (this.ship.invuln > 0) this.ship.invuln -= 1;

    this.handleHyperspace();
    this.updateShip();
    this.fireIfRequested();
    this.advanceBullets();
    this.applyGravity();
    this.advanceAsteroids();
    this.handleCollisions();

    if (this.asteroids.length === 0) {
      this.phase = 'wave-clear';
      this.waveBeatLeft = WAVE_BEAT_TICKS;
      this.well.active = false;
      this.ctx.audio.sfx('wave-up');
    }
  }

  render(g: CanvasRenderingContext2D): void {
    // Background.
    g.fillStyle = FIELD_BG;
    g.fillRect(0, 0, WIDTH, HEIGHT);

    // Gravity well — render behind everything so asteroids tracing past
    // it are visually in front. Bloom + concentric ring "event horizon."
    if (this.well.active) this.drawGravityWell(g);

    // Asteroids, bullets, then ship (top of stack).
    for (const a of this.asteroids) this.drawAsteroid(g, a);
    for (const b of this.bullets) this.drawBullet(g, b);
    if (this.phase !== 'crashed' || this.crashFlashLeft % 4 < 2) {
      this.drawShip(g);
    }

    // Crash flash overlay.
    if (this.phase === 'crashed' && this.crashFlashLeft > 0) {
      const a = this.crashFlashLeft / CRASH_FLASH_TICKS;
      g.globalAlpha = a * 0.5;
      g.fillStyle = HYPERSPACE_FLASH;
      g.fillRect(0, 0, WIDTH, HEIGHT);
      g.globalAlpha = 1;
    }
    // Hyperspace flash (briefer + magenta).
    if (this.hyperspaceFlashLeft > 0) {
      const a = this.hyperspaceFlashLeft / HYPERSPACE_FLASH_TICKS;
      g.globalAlpha = a * 0.6;
      g.fillStyle = HYPERSPACE_FLASH;
      g.fillRect(0, 0, WIDTH, HEIGHT);
      g.globalAlpha = 1;
    }

    this.renderHUD(g);

    if (this.phase === 'game-over') this.renderGameOver(g);
    else if (this.waveBannerLeft > 0 && this.phase === 'playing') this.renderWaveBanner(g);
  }

  resize(_w: number, _h: number): void {
    /* 960x540 internal — cabinet scales */
  }

  pause(): void {
    // While the runtime loop is suspended, update() doesn't fire — so
    // the thrust drone would keep playing at whatever gain was last
    // written. Mute it; resume() restores normal updateThrustAudio()
    // behavior on the next update() tick.
    this.thrustLoop?.setGain(0);
  }

  resume(): void {
    // updateThrustAudio() will set the correct gain on the next update().
  }

  teardown(): void {
    this.bullets = [];
    this.asteroids = [];
    this.thrustLoop?.stop();
    this.thrustLoop = null;
  }

  getScore(): number {
    return Math.floor(this.score);
  }

  getFinalScore(): number {
    return this.phase === 'game-over' ? this.finalScore : Math.floor(this.score);
  }

  isGameOver(): boolean {
    return this.phase === 'game-over';
  }

  shouldRestart(): boolean {
    return this.phase === 'game-over' && this.restartArmed;
  }

  // --------------------------------------------------------------------------
  // Audio — sustained voices
  // --------------------------------------------------------------------------

  /**
   * Drive the thrust drone. Lazy-init the loop on first call, then
   * modulate gain to 0 (silent) when not thrusting / crashed / game-over,
   * and to THRUST_LOOP_GAIN when held during normal play. The loop
   * itself stays alive across phase transitions to avoid the click/pop
   * that comes with create/destroy each press.
   *
   * Pitch is a tiny constant — the rumble shouldn't vary with anything
   * that breaks the "low-frequency engine" feel.
   */
  private updateThrustAudio(): void {
    if (!this.thrustLoop) {
      this.thrustLoop = this.ctx.audio.loop({
        wave: 'sawtooth',
        f: 85,
        gain: 0,
        fm: { ratio: 0.5, index: 18 },
      });
    }
    const wantsAudio =
      this.inThrust && (this.phase === 'playing' || this.phase === 'wave-clear');
    this.thrustLoop.setGain(wantsAudio ? 0.14 : 0);
  }

  // --------------------------------------------------------------------------
  // Ship + input
  // --------------------------------------------------------------------------

  private updateShip(): void {
    const s = this.ship;
    if (this.inLeft && !this.inRight) s.rot -= SHIP_ROT;
    else if (this.inRight && !this.inLeft) s.rot += SHIP_ROT;

    if (this.inThrust) {
      s.vx += Math.cos(s.rot) * SHIP_THRUST;
      s.vy += Math.sin(s.rot) * SHIP_THRUST;
      const speed = Math.hypot(s.vx, s.vy);
      if (speed > SHIP_MAX_VEL) {
        const k = SHIP_MAX_VEL / speed;
        s.vx *= k;
        s.vy *= k;
      }
    }
    s.x = wrap(s.x + s.vx, WIDTH);
    s.y = wrap(s.y + s.vy, HEIGHT);
  }

  private fireIfRequested(): void {
    if (!this.firePressed) return;
    this.firePressed = false; // single-shot per justPressed event
    if (this.fireCooldown > 0) return;
    if (this.bullets.length >= BULLET_MAX) return;
    const s = this.ship;
    this.bullets.push({
      // Spawn just in front of the nose so the bullet doesn't visually
      // emit from the cockpit.
      x: s.x + Math.cos(s.rot) * (SHIP_RADIUS + 2),
      y: s.y + Math.sin(s.rot) * (SHIP_RADIUS + 2),
      vx: Math.cos(s.rot) * BULLET_VEL + s.vx * 0.4,
      vy: Math.sin(s.rot) * BULLET_VEL + s.vy * 0.4,
      life: BULLET_LIFE,
      born: this.tickCount,
    });
    this.fireCooldown = SHIP_FIRE_COOLDOWN;
    this.ctx.audio.sfx('shoot');
  }

  private handleHyperspace(): void {
    if (!this.hyperspacePressed) return;
    this.hyperspacePressed = false;
    if (this.hyperspaceCooldown > 0) return;
    if (this.bullets.length === 0) return;

    // Find the OLDEST in-flight bullet (smallest `born`).
    let oldestIdx = 0;
    for (let i = 1; i < this.bullets.length; i++) {
      const bi = this.bullets[i];
      const bo = this.bullets[oldestIdx];
      if (bi && bo && bi.born < bo.born) oldestIdx = i;
    }
    const target = this.bullets[oldestIdx];
    if (!target) return;

    // Teleport. Velocity zeros so the player doesn't instantly fly out of
    // a freshly-cleared pocket; rotation kept so the player retains aim.
    this.ship.x = target.x;
    this.ship.y = target.y;
    this.ship.vx = 0;
    this.ship.vy = 0;
    this.ship.invuln = Math.max(this.ship.invuln, 20);

    this.bullets.splice(oldestIdx, 1);
    this.hyperspaceCooldown = HYPERSPACE_COOLDOWN;
    this.hyperspaceFlashLeft = HYPERSPACE_FLASH_TICKS;
    this.ctx.audio.sfx('hyperspace');
  }

  private respawn(): void {
    this.ship.x = WIDTH / 2;
    this.ship.y = HEIGHT / 2;
    this.ship.vx = 0;
    this.ship.vy = 0;
    this.ship.rot = -Math.PI / 2;
    this.ship.invuln = RESPAWN_INVULN;
  }

  // --------------------------------------------------------------------------
  // Bullets + asteroids
  // --------------------------------------------------------------------------

  private advanceBullets(): void {
    for (const b of this.bullets) {
      b.x = wrap(b.x + b.vx, WIDTH);
      b.y = wrap(b.y + b.vy, HEIGHT);
      b.life -= 1;
    }
    this.bullets = this.bullets.filter((b) => b.life > 0);
  }

  private advanceAsteroids(): void {
    for (const a of this.asteroids) {
      a.x = wrap(a.x + a.vx, WIDTH);
      a.y = wrap(a.y + a.vy, HEIGHT);
      a.rot += a.rotVel;
    }
  }

  private applyGravity(): void {
    if (!this.well.active) return;
    const wx = this.well.x;
    const wy = this.well.y;
    // Ship pull.
    {
      const dx = wx - this.ship.x;
      const dy = wy - this.ship.y;
      const r = Math.max(GRAVITY_MIN_R, Math.hypot(dx, dy));
      const f = Math.min(GRAVITY_MAX_FORCE, GRAVITY_CONST / (r * r));
      this.ship.vx += (dx / r) * f;
      this.ship.vy += (dy / r) * f;
    }
    // Asteroid pull.
    for (const a of this.asteroids) {
      const dx = wx - a.x;
      const dy = wy - a.y;
      const r = Math.max(GRAVITY_MIN_R, Math.hypot(dx, dy));
      const f = Math.min(GRAVITY_MAX_FORCE, GRAVITY_CONST / (r * r));
      a.vx += (dx / r) * f * 0.6; // asteroids are heavier than the ship
      a.vy += (dy / r) * f * 0.6;
    }
  }

  private handleCollisions(): void {
    // Bullets vs asteroids.
    outer: for (let bi = this.bullets.length - 1; bi >= 0; bi--) {
      const b = this.bullets[bi];
      if (!b) continue;
      for (let ai = this.asteroids.length - 1; ai >= 0; ai--) {
        const a = this.asteroids[ai];
        if (!a) continue;
        const r = ASTEROID_RADIUS[a.tier];
        if (circleHit(b.x, b.y, BULLET_RADIUS, a.x, a.y, r)) {
          // Score, split, remove bullet.
          this.score += ASTEROID_SCORE[a.tier];
          this.asteroids.splice(ai, 1);
          this.splitAsteroid(a, b);
          this.bullets.splice(bi, 1);
          this.ctx.audio.sfx('crack');
          continue outer;
        }
      }
    }

    // Ship vs asteroids (respect invuln).
    if (this.ship.invuln > 0) return;
    for (const a of this.asteroids) {
      const r = ASTEROID_RADIUS[a.tier];
      if (circleHit(this.ship.x, this.ship.y, SHIP_RADIUS, a.x, a.y, r)) {
        this.lives -= 1;
        this.phase = 'crashed';
        this.crashFlashLeft = CRASH_FLASH_TICKS;
        this.ctx.events.emit('death', { remainingLives: Math.max(0, this.lives) });
        this.ctx.audio.sfx('crash');
        return;
      }
    }
  }

  private splitAsteroid(parent: Asteroid, hitBy: Vec): void {
    if (parent.tier === 2) return; // small asteroids vanish

    // Each child spawns at the parent's position, with velocity = parent
    // velocity rotated +/-30 degrees plus a kick away from the impact.
    const childTier = (parent.tier + 1) as 0 | 1 | 2;
    const speed = Math.max(1.2, Math.hypot(parent.vx, parent.vy)) * 1.15;
    const baseAngle = Math.atan2(parent.y - hitBy.y, parent.x - hitBy.x);
    for (const offset of [-0.5, 0.5]) {
      const angle = baseAngle + offset;
      this.asteroids.push(makeAsteroid(
        this.ctx,
        parent.x,
        parent.y,
        Math.cos(angle) * speed,
        Math.sin(angle) * speed,
        childTier
      ));
    }
  }

  // --------------------------------------------------------------------------
  // Wave management
  // --------------------------------------------------------------------------

  private spawnWave(wave: number): void {
    this.asteroids = [];
    const count = WAVE_START_COUNT + (wave - 1) * WAVE_GROWTH;
    for (let i = 0; i < count; i++) {
      // Spawn near a screen edge, moving roughly toward the center so the
      // player has time to react before the asteroid arrives.
      const side = this.ctx.random.int(0, 4);
      let x = 0;
      let y = 0;
      if (side === 0) { x = randFloat(this.ctx, 0, WIDTH); y = 0; }
      else if (side === 1) { x = WIDTH; y = randFloat(this.ctx, 0, HEIGHT); }
      else if (side === 2) { x = randFloat(this.ctx, 0, WIDTH); y = HEIGHT; }
      else { x = 0; y = randFloat(this.ctx, 0, HEIGHT); }

      const cx = WIDTH / 2;
      const cy = HEIGHT / 2;
      const dx = cx - x;
      const dy = cy - y;
      const speed = ASTEROID_BASE_SPEED + randFloat(this.ctx, 0, ASTEROID_SPEED_VAR);
      // Add some angular wobble so spawns aren't all dead-aimed at center.
      const wobble = randFloat(this.ctx, -0.6, 0.6);
      const ang = Math.atan2(dy, dx) + wobble;
      const vx = Math.cos(ang) * speed;
      const vy = Math.sin(ang) * speed;
      this.asteroids.push(makeAsteroid(this.ctx, x, y, vx, vy, 0));
    }

    // Gravity well: spawn on every WAVE_GRAVITY_EVERY wave at a point
    // offset from the screen center.
    if (wave % GRAVITY_WAVE_EVERY === 0) {
      this.well.active = true;
      // Offset between 30% and 70% of width / height so the well is not
      // dead-center but also not in a corner.
      this.well.x = randFloat(this.ctx, WIDTH * 0.3, WIDTH * 0.7);
      this.well.y = randFloat(this.ctx, HEIGHT * 0.3, HEIGHT * 0.7);
      this.ctx.audio.sfx('well');
    } else {
      this.well.active = false;
    }
  }

  // --------------------------------------------------------------------------
  // Rendering
  // --------------------------------------------------------------------------

  private drawShip(g: CanvasRenderingContext2D): void {
    const s = this.ship;
    // Invulnerable blink — show every other 4-tick window.
    if (s.invuln > 0 && s.invuln % 8 < 4) return;

    const cosR = Math.cos(s.rot);
    const sinR = Math.sin(s.rot);
    // Local triangle: nose, left rear, right rear.
    const local: ReadonlyArray<readonly [number, number]> = [
      [SHIP_RADIUS, 0],
      [-SHIP_RADIUS * 0.7, SHIP_RADIUS * 0.6],
      [-SHIP_RADIUS * 0.4, 0],
      [-SHIP_RADIUS * 0.7, -SHIP_RADIUS * 0.6],
    ];
    const pts = local.map<readonly [number, number]>(([lx, ly]) => [
      s.x + lx * cosR - ly * sinR,
      s.y + lx * sinR + ly * cosR,
    ]);

    // Bloom underlay.
    g.lineCap = 'round';
    g.lineJoin = 'round';
    g.lineWidth = 3;
    g.strokeStyle = SHIP_BLOOM;
    strokePolygon(g, pts);

    // Bright pass.
    g.lineWidth = 1.5;
    g.strokeStyle = SHIP_LINE;
    strokePolygon(g, pts);

    // Thruster flame — drawn only when thrust held, alternates per tick for
    // a flicker that doesn't depend on wall-clock (still deterministic since
    // it's `tickCount % 2`).
    if (this.inThrust && this.tickCount % 2 === 0) {
      const tailX = s.x - cosR * SHIP_RADIUS * 0.7;
      const tailY = s.y - sinR * SHIP_RADIUS * 0.7;
      const flameLen = SHIP_RADIUS * 0.9;
      const tipX = tailX - cosR * flameLen;
      const tipY = tailY - sinR * flameLen;
      g.lineWidth = 2;
      g.strokeStyle = THRUSTER;
      g.beginPath();
      g.moveTo(tailX, tailY);
      g.lineTo(tipX, tipY);
      g.stroke();
    }
  }

  private drawAsteroid(g: CanvasRenderingContext2D, a: Asteroid): void {
    const r = ASTEROID_RADIUS[a.tier];
    const verts = ASTEROID_VERTS[a.tier];
    const cosR = Math.cos(a.rot);
    const sinR = Math.sin(a.rot);
    const pts: Array<readonly [number, number]> = [];
    for (let i = 0; i < verts; i++) {
      const theta = (i / verts) * TWO_PI;
      const jr = r * (a.vertJitter[i] ?? 1);
      const lx = Math.cos(theta) * jr;
      const ly = Math.sin(theta) * jr;
      pts.push([a.x + lx * cosR - ly * sinR, a.y + lx * sinR + ly * cosR]);
    }
    g.lineCap = 'round';
    g.lineJoin = 'round';
    g.lineWidth = 3;
    g.strokeStyle = VECTOR_BLOOM;
    strokePolygon(g, pts);
    g.lineWidth = 1.5;
    g.strokeStyle = VECTOR_LINE;
    strokePolygon(g, pts);
  }

  private drawBullet(g: CanvasRenderingContext2D, b: Bullet): void {
    g.fillStyle = BULLET;
    g.beginPath();
    g.arc(b.x, b.y, BULLET_RADIUS, 0, TWO_PI);
    g.fill();
  }

  private drawGravityWell(g: CanvasRenderingContext2D): void {
    const t = this.tickCount;
    // Two concentric pulsing rings + a center dot.
    g.lineCap = 'round';
    g.lineJoin = 'round';
    for (let i = 0; i < 3; i++) {
      const baseR = 18 + i * 14;
      const pulse = (Math.sin((t + i * 18) * 0.1) + 1) * 4;
      const radius = baseR + pulse;
      g.lineWidth = 4;
      g.strokeStyle = GRAVITY_WELL_BLOOM;
      g.beginPath();
      g.arc(this.well.x, this.well.y, radius, 0, TWO_PI);
      g.stroke();
      g.lineWidth = 1.5;
      g.strokeStyle = GRAVITY_WELL;
      g.beginPath();
      g.arc(this.well.x, this.well.y, radius, 0, TWO_PI);
      g.stroke();
    }
    g.fillStyle = GRAVITY_WELL;
    g.beginPath();
    g.arc(this.well.x, this.well.y, 3, 0, TWO_PI);
    g.fill();
  }

  private renderHUD(g: CanvasRenderingContext2D): void {
    g.font = '16px ui-monospace, monospace';
    g.textBaseline = 'top';

    g.fillStyle = HUD_TEXT;
    g.textAlign = 'left';
    g.fillText('SCORE', 16, 12);
    g.fillStyle = HUD_SCORE;
    g.fillText(String(Math.floor(this.score)).padStart(6, '0'), 16, 30);

    g.fillStyle = HUD_TEXT;
    g.textAlign = 'center';
    g.fillText('WAVE', WIDTH / 2, 12);
    g.fillStyle = HUD_WAVE;
    g.fillText(String(this.wave), WIDTH / 2, 30);

    g.fillStyle = HUD_TEXT;
    g.textAlign = 'right';
    g.fillText('LIVES', WIDTH - 16, 12);
    g.fillStyle = HUD_LIVES;
    g.fillText('▲'.repeat(Math.max(0, this.lives)), WIDTH - 16, 30);

    // Hyperspace cooldown indicator (lower-left).
    g.textAlign = 'left';
    g.fillStyle = HUD_TEXT;
    g.fillText('HYPER', 16, HEIGHT - 32);
    const cdFrac = 1 - this.hyperspaceCooldown / HYPERSPACE_COOLDOWN;
    g.fillStyle = this.hyperspaceCooldown === 0 ? HUD_SCORE : HUD_TEXT;
    g.fillRect(16, HEIGHT - 14, 80 * cdFrac, 4);
    g.strokeStyle = HUD_TEXT;
    g.lineWidth = 1;
    g.strokeRect(16, HEIGHT - 14, 80, 4);
  }

  private renderWaveBanner(g: CanvasRenderingContext2D): void {
    g.font = '28px ui-monospace, monospace';
    g.textAlign = 'center';
    g.fillStyle = HUD_WAVE;
    g.fillText(`WAVE ${this.wave}`, WIDTH / 2, HEIGHT / 2 - 80);
    if (this.well.active) {
      g.font = '14px ui-monospace, monospace';
      g.fillStyle = GRAVITY_WELL;
      g.fillText('GRAVITY WELL DETECTED', WIDTH / 2, HEIGHT / 2 - 50);
    }
  }

  private renderGameOver(g: CanvasRenderingContext2D): void {
    g.font = '36px ui-monospace, monospace';
    g.textAlign = 'center';
    g.fillStyle = HUD_SCORE;
    g.fillText('GAME OVER', WIDTH / 2, HEIGHT / 2 - 30);
    g.font = '18px ui-monospace, monospace';
    g.fillStyle = HUD_TEXT;
    g.fillText(`FINAL SCORE  ${this.finalScore}`, WIDTH / 2, HEIGHT / 2 + 10);
    g.fillText(`WAVE  ${this.finalWave}`, WIDTH / 2, HEIGHT / 2 + 36);
    if (this.tickCount % 60 < 40) {
      g.fillStyle = HUD_WAVE;
      g.fillText('PRESS Z TO RETRY', WIDTH / 2, HEIGHT / 2 + 72);
    }
  }
}

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------

/**
 * Uniform float in [min, max). PRNG only exposes `next()` (uniform [0,1)),
 * so every float-range draw goes through this helper to keep the
 * determinism contract intact (one `next()` call per `randFloat` call).
 */
function randFloat(ctx: GameContext, min: number, max: number): number {
  return min + ctx.random.next() * (max - min);
}

function makeAsteroid(
  ctx: GameContext,
  x: number,
  y: number,
  vx: number,
  vy: number,
  tier: 0 | 1 | 2
): Asteroid {
  const verts = ASTEROID_VERTS[tier];
  const jitter: number[] = [];
  for (let i = 0; i < verts; i++) jitter.push(randFloat(ctx, 0.78, 1.18));
  return {
    x,
    y,
    vx,
    vy,
    tier,
    rot: randFloat(ctx, 0, TWO_PI),
    rotVel: randFloat(ctx, -0.02, 0.02),
    vertJitter: Object.freeze(jitter),
  };
}

function strokePolygon(
  g: CanvasRenderingContext2D,
  pts: ReadonlyArray<readonly [number, number]>
): void {
  if (pts.length === 0) return;
  g.beginPath();
  const first = pts[0];
  if (!first) return;
  g.moveTo(first[0], first[1]);
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i];
    if (p) g.lineTo(p[0], p[1]);
  }
  g.closePath();
  g.stroke();
}

function wrap(v: number, max: number): number {
  if (v < 0) return v + max;
  if (v >= max) return v - max;
  return v;
}

function circleHit(
  ax: number,
  ay: number,
  ar: number,
  bx: number,
  by: number,
  br: number
): boolean {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy <= (ar + br) * (ar + br);
}
